import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import MaterialExtraForm from '../components/MaterialExtraForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';

const API_PREFIX = '/internal/material-extras/global';

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const values = Object.values(name);
    return values.find(v => v) || '';
  }
  return String(name);
};

export default function AdminMaterialExtras() {
  const [materialExtras, setMaterialExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMaterialExtra, setEditingMaterialExtra] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
  });

  const { showToast } = useToast();

  const fetchMaterialExtras = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/material-extras?ownership=system');
      setMaterialExtras(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch system material extras');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterialExtras();
  }, [fetchMaterialExtras]);

  const filteredMaterialExtras = (() => {
    let filtered = [...materialExtras];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(m => {
        const name = getDisplayName(m.name);
        return (
          name?.toLowerCase().includes(search) ||
          m.key?.toLowerCase().includes(search)
        );
      });
    }

    filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    return filtered;
  })();

  const handleAdd = () => {
    setEditingMaterialExtra(null);
    setShowForm(true);
  };

  const handleEdit = (materialExtra) => {
    setEditingMaterialExtra(materialExtra);
    setShowForm(true);
  };

  const handleDelete = (materialExtra) => {
    setDeleteTarget(materialExtra);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast(`Material extra "${getDisplayName(deleteTarget.name)}" deactivated`, 'success');
      setDeleteTarget(null);
      fetchMaterialExtras();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete material extra', 'error');
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingMaterialExtra;
    if (isEditing) {
      await api.patch(`${API_PREFIX}/${editingMaterialExtra._id}`, data);
    } else {
      await api.post(API_PREFIX, data);
    }
    setShowForm(false);
    setEditingMaterialExtra(null);
    fetchMaterialExtras();
    showToast(
      isEditing ? 'System material extra updated successfully!' : 'System material extra created successfully!',
      'success'
    );
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (val) => {
        if (typeof val === 'object') {
          return Object.keys(val).map((lang) => `<strong>${lang}:</strong> ${val[lang]}`).join('<br/>');
        }
        return val;
      }
    },
    { key: 'key', label: 'Key', sortable: true },
    {
      key: 'materialCostMultiplier',
      label: 'Cost Multiplier',
      render: (val) => val ? `${Number(val).toFixed(2)}x` : '1.00x'
    },
    {
      key: 'showInSite',
      label: 'Show in Site',
      render: (val) => (val ? '✓' : '✗')
    },
    { key: 'displayOrder', label: 'Order', sortable: true },
    {
      key: 'createdAt',
      label: 'Created',
      render: (val) => new Date(val).toLocaleDateString()
    }
  ];

  const renderFilters = () => (
    <div className="filters" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name or key..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ flex: '1 1 300px' }}
        />
      </div>
    </div>
  );

  if (loading && materialExtras.length === 0) return <div>Loading...</div>;
  if (error && materialExtras.length === 0) return <div className="error">{error}</div>;

  return (
    <div>
      {showForm ? (
        <MaterialExtraForm
          materialExtra={editingMaterialExtra}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingMaterialExtra(null);
          }}
        />
      ) : (
        <DataTable
          title="System Material Extra Presets"
          data={filteredMaterialExtras}
          columns={columns}
          allowHTML={true}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          renderFilters={renderFilters}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete System Material Extra"
          message={`Are you sure you want to deactivate "${getDisplayName(deleteTarget.name)}"? Operators who already duplicated it will not be affected.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmText="Deactivate"
        />
      )}
    </div>
  );
}
